/**
 * Unread mentions, in the top right where a person looks for them.
 *
 * A nav item tells you a page EXISTS; it does not tell you something is
 * waiting on it, and nobody opens a page on the off chance a colleague needed
 * them. The count has to come to you.
 *
 * IT HOLDS NO COUNT OF ITS OWN. It used to, and that was the bug: marking
 * everything read on the Mentions page updated that page's copy while the bell
 * kept its own until the next two-minute poll, so the badge sat there for
 * something already dealt with. The count now lives in mentionStore, which
 * every consumer subscribes to, so the badge clears on the click that cleared
 * it.
 *
 * THE POLL IS ONLY FOR NEW ONES. Every two minutes, paused while the tab is
 * hidden, and it catches up the moment somebody returns. A mention is not an
 * outage; a live Firestore listener on every open dashboard would cost a
 * connection per user all day to deliver a number that changes twice a week.
 */

import { useEffect, useRef } from 'react';
import { useMentions, refresh } from '../mentionStore';

const POLL_MS = 120_000;

export function MentionBell({ orgId, onOpen }: { orgId: string | null; onOpen: () => void }) {
  const { unread } = useMentions();
  const timer = useRef<number | null>(null);

  useEffect(() => {
    void refresh(orgId);
    timer.current = window.setInterval(() => {
      if (!document.hidden) void refresh(orgId);
    }, POLL_MS);

    const onVisible = () => { if (!document.hidden) void refresh(orgId); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [orgId]);

  const label = unread === 0 ? 'Mentions' : `${unread} unread mention${unread === 1 ? '' : 's'}`;

  return (
    <button type="button" className="bell" onClick={onOpen} title={label} aria-label={label}>
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
           strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {unread > 0 && (
        // Capped at 9+. Two digits in a 16px circle is unreadable, and the
        // difference between 12 and 40 changes nothing about what you do next.
        <span className="bell-count">{unread > 9 ? '9+' : unread}</span>
      )}
    </button>
  );
}
